import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, RefreshCw, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from '../hooks/use-toast';
import { getQuotes, deleteQuote } from '../lib/quotes';

const QUOTE_TYPE_LABELS = {
  passive: 'Passive Cable',
  marine: 'Marine Cable',
  dualmode: 'Dual Mode Cable',
  configuration: 'Configuration',
};

const SavedQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQuotes();
      setQuotes(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await deleteQuote(id);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast({ title: 'Deleted', description: 'Quote removed' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const downloadJSON = (quote) => {
    const blob = new Blob([JSON.stringify(quote, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quote-${quote.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-lg">
        <CardContent className="py-12 text-center text-slate-500">
          Loading saved quotes...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-800">Saved Quotes</h2>
        <Button variant="outline" onClick={fetchQuotes} size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {quotes.length === 0 ? (
        <Card className="border-slate-200 shadow-lg">
          <CardContent className="py-12 text-center text-slate-500">
            No saved quotes yet. Use the "Save Quote" button in any calculator to save one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {quotes.map((q) => (
            <Card key={q.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{q.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {QUOTE_TYPE_LABELS[q.quote_type] || q.quote_type} &middot;{' '}
                      {new Date(q.created_at).toLocaleDateString()} {new Date(q.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => downloadJSON(q)} title="Download JSON">
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(q.id)} title="Delete" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Total Price</span>
                    <p className="font-semibold text-lg text-emerald-700">
                      ${Number(q.pricing?.totalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  {q.configuration?.numElectrodes && (
                    <div>
                      <span className="text-slate-500">Electrodes</span>
                      <p className="font-medium">{q.configuration.numElectrodes}</p>
                    </div>
                  )}
                  {q.configuration?.interval && (
                    <div>
                      <span className="text-slate-500">Interval</span>
                      <p className="font-medium">{q.configuration.interval} m</p>
                    </div>
                  )}
                  {q.weight?.pounds && (
                    <div>
                      <span className="text-slate-500">Weight</span>
                      <p className="font-medium">{Number(q.weight.pounds).toFixed(1)} lbs</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedQuotes;
